import store from '@/store/index';
import {
  VuexModule,
  Module,
  getModule,
  Mutation,
  Action,
} from 'vuex-module-decorators';
import {
  Coverage,
  Majority,
  MajorityModel,
  MajorityRequest,
  OpenRequestModal,
  RequestCoverageForm,
  RequestExclusionForm,
} from './majority-types';
import {
  defaultCoverageForm,
  defaultExclusionForm,
  defaultFilter,
  defaultRequestForm
} from './majority-factory';

import MajorityService from '@/services-http/servicos/MajorityService';
import CoverageService from '@/services-http/sdi/CoverageReasonService';
import Formatters from '@/shared/formatters/formatters';
import moment from 'moment';

const formatter = new Formatters();

@Module({
  namespaced: true,
  name: 'majority',
  dynamic: true,
  store,
})
class MajorityStore extends VuexModule {
  data: MajorityModel = new MajorityModel();

  // Getters

  get automaticSearchFields() {
    return [
      'competenceMonth',
      'competenceYear',
      'name',
      'cpf',
      'majorityStatus',
      'page',
      'size',
    ];
  }

  get beneficiary() {
    return this.data.beneficiary;
  }

  get showModal() {
    return this.data.showModal;
  }

  get monthsAdvances() {
    return this.data.monthsAdvances;
  }

  get coverages() {
    return this.data.coverages;
  }

  get loadPage() {
    return this.data.loadingPage;
  }

  get majorities() {
    return this.data.majorities;
  }

  get majorityProtocols() {
    return this.data.protocols;
  }

  get majorityFilter() {
    return this.data.filter;
  }

  get majorityStatus() {
    return this.data.majorityStatus;
  }

  get pageMounted() {
    return this.data.pageMounted;
  }

  get majoritiesTotalPages() {
    return this.data.totalPages;
  }

  get majoritiesTotalElements() {
    return this.data.totalElements;
  }

  get majorityFilterCounter() {
    const {
      competenceMonth,
      competenceYear,
      name,
      cpf,
      majorityStatus,
      page,
      size,
      sort,
      ...filter
    } = this.data.filter;

    return Object.values(filter).filter(value =>
      value !== null &&
      value !== undefined &&
      !(typeof value === 'string' && value.trim() === '') &&
      !(Array.isArray(value) && value.length === 0)
    ).length;
  }

  get requestForm() {
    return this.data.requestForm;
  }

  get showRequestModal() {
    return this.data.showExportRequestModal;
  }

  get showConfirmModal() {
    return this.data.showExportConfirmModal;
  }

  get filterWithFilledFields() {
    return formatter.getFilledKeysFromObject(this.majorityFilter);
  }

  get exportFilterFields() {
    const allowedFields = [
      'competenceMonth', 'competenceYear', 'name',
      'cpf', 'financialGroupId', 'contractId',
      'subContractId', 'carrierId', 'monthsAdvance',
      'permanence', 'majorityStatus', 'externalProtocol'
    ];

    return Object.fromEntries(
      Object.entries(this.filterWithFilledFields)
        .filter(([key]) => allowedFields.includes(key))
    );
  }

  get reprocessFilterFields() {
    const allowedFields = [
      'competenceMonth', 'competenceYear', 'name',
      'cpf', 'financialGroupId', 'contractId',
      'subContractId', 'carrierId', 'monthsAdvance',
      'permanence', 'majorityStatus'
    ];

    return Object.fromEntries(
      Object.entries(this.filterWithFilledFields)
        .filter(([key]) => allowedFields.includes(key))
    );
  }

  get protocolsFields() {
    const allowedFields = [
      'competenceMonth', 'competenceYear', 'contractId',
    ];

    return Object.fromEntries(
      Object.entries(this.filterWithFilledFields)
        .filter(([key]) => allowedFields.includes(key))
    );
  }

  get majorityTab() {
    return this.data.majorityTab;
  }

  // Actions

  @Action
  exportReport() {
    const majorityService = new MajorityService();
    return majorityService.Export(this.exportFilterFields);
  }

  @Action
  async requestReprocess() {
    try {
      this.loadingPage(true);

      const majorityService = new MajorityService();
      await majorityService.Reprocess(this.reprocessFilterFields);

      setTimeout(async () => {
        await this.search();

        this.loadingPage(false);
      }, 2000);
      return true;
    } catch(error) {
      return error;
    }
  }

  @Action
  async fetchMajorities() {
    try {
      const paramString = Object.entries(this.filterWithFilledFields)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
      const majorityService = new MajorityService();
      const result = await majorityService.FetchMajorities(paramString);

      const majorities = result.data.content as Majority[];

      this.SET_MAJORITIES(majorities);
      this.SET_TOTAL_PAGES(result.data.totalPages);
      this.SET_TOTAL_ELEMENTS(result.data.totalElements);
    } catch(error) {
      return error;
    }
  }

  @Action
  async fetchCoverages() {
    try {
      const coverageService = new CoverageService();
      const result = await coverageService.FetchCoverage();

      const coverages = result.data as Coverage[];

      this.SET_COVERAGES(coverages);
    } catch(error) {
      return error;
    }
  }

  @Action
  async requestCoverage() {
    const majorityService = new MajorityService();
    let payload = {...this.data.requestForm} as RequestCoverageForm;
    payload.coverageExtensionLimitDate = payload.coverageExtensionLimitDate
      ? moment(payload.coverageExtensionLimitDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
      : null;

    return await majorityService.RequestCoverage(payload, this.data.beneficiary?.id);
  }

  @Action
  async requestExclusion() {
    const majorityService = new MajorityService();
    let payload = {...this.data.requestForm} as RequestExclusionForm;
    payload.endDate = moment(payload.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD');

    return await majorityService.RequestExclusion(payload, this.data.beneficiary?.id);
  }

  @Action
  async search() {
    try {
      if(this.data.pageMounted) this.loadingPage(true);

      await this.fetchMajorities();

      if(this.data.pageMounted) this.loadingPage(false);
    } catch(error) {
      return error;
    }
  }

    @Action
  async fetchParameters() {
    try {
      this.loadingPage(true);

      this.data.filter.externalProtocol = null;
      this.data.protocols = [];

      const paramString = Object.entries(this.protocolsFields)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');
        const majorityService = new MajorityService();
        const response = await majorityService.FetchProtocols(paramString);
        const protocols = response &&
                          response.data &&
                          response.data.length > 0
                          ? response.data
                          : [];

        if (protocols && protocols.length > 0) {
          this.SET_MAJORITY_PROTOCOLS(protocols);
        }

      this.loadingPage(false);
    } catch(error) {
      return error;
    }
  }

  @Action
  async resetFilter(isRhprotegido: boolean) {
    this.RESET_FILTER(isRhprotegido);

    await this.search();
  }

  @Action
  async setMajorityFilter(filter: Partial<MajorityRequest>) {
    this.SET_FILTER(filter);

    if (this.automaticSearchFields.some((key) => key in filter)) {
      await this.search();
    }
  }

  // Mutations

  @Mutation
  showExportRequestModal(show: boolean) {
    this.data.showExportRequestModal = show;
  }

  @Mutation
  showExportConfirmModal(show: boolean) {
    this.data.showExportConfirmModal = show;
  }

  @Mutation
  setRequestForm(form: Partial<RequestCoverageForm> | Partial<RequestExclusionForm> | {}) {
    this.data.requestForm = {
      ...this.data.requestForm,
      ...form,
    }
  }

  @Mutation
  setMajorityTab(tab: 'majority' | 'reports') {
    this.data.majorityTab = tab;
  }

  @Mutation
  setPageMounted(mounted: boolean) {
    this.data.pageMounted = mounted;
  }

  @Mutation
  loadingPage(loading: boolean) {
    this.data.loadingPage = loading;
  }

  @Mutation
  openRequestModal(payload: OpenRequestModal) {
    const { majorityStatus, beneficiary } = payload;

    this.data.beneficiary = beneficiary;
    this.data.majorityStatus = majorityStatus;
    this.data.showModal = true;

    if (this.data.majorityStatus === 'COVERAGE') {
      this.data.requestForm = defaultCoverageForm();
    };

    if (this.data.majorityStatus === 'EXCLUSION') {
      this.data.requestForm = defaultExclusionForm();
    }
  }

  @Mutation
  hideRequestModal() {
    this.data.showModal = false;
    this.data.majorityStatus = null;
    this.data.beneficiary = null;
    this.data.requestForm = defaultRequestForm();
  }

  @Mutation
  exportMajorities(params: MajorityRequest) {
    // service
  }

  @Mutation
  reprocessMajorities(params: MajorityRequest) {
    // service
  }

  @Mutation
  resetModule() {
    this.data = new MajorityModel();
  }

  //  Private Mutations

  @Mutation
  private SET_MAJORITY_PROTOCOLS(protocols: []) {
    const majorityProtocols = protocols.map((protocol) => parseInt(protocol));
    majorityProtocols.sort((a, b) => b - a);

    this.data.protocols = majorityProtocols;
  }

  @Mutation
  private RESET_FILTER(isRhprotegido: boolean) {
    const filterEmpty = defaultFilter();
    const filter = {
      ...filterEmpty,
      financialGroupId: isRhprotegido ? this.data.filter.financialGroupId : null,
    }

    this.data.filter = filter;
  }

  @Mutation
  private SET_FILTER(filter: Partial<MajorityRequest>) {
    this.data.filter = {
      ...this.data.filter,
      ...filter,
    }
  }

  @Mutation
  private SET_MAJORITIES(majorities: Majority[]) {
    this.data.majorities = majorities;
  }

  @Mutation
  private SET_TOTAL_PAGES(totalPages: number) {
    this.data.totalPages = totalPages;
  }

  @Mutation
  private SET_TOTAL_ELEMENTS(totalElements: number) {
    this.data.totalElements = totalElements;
  }

  @Mutation
  private SET_COVERAGES(coverages: Coverage[]) {
    const formattedCoverages = coverages
    .slice()
    .sort((a, b) => a.description.localeCompare(b.description))
    .map(coverage => (
      {
        ...coverage,
        description: formatter.capitalizeText(coverage.description),
      }
    ));

    this.data.coverages = formattedCoverages;
  }
}

export default getModule(MajorityStore);